/**
 * contracts/adapter.md §5 — bytes → text. The adapter's single largest correctness
 * responsibility, because a wrong guess on a windows-1256 file produces mojibake that looks
 * like file corruption rather than a settable option.
 *
 * DETECTION ORDER, first match wins:
 *   1. user       an explicit override from the picker            confidence 1.0
 *   2. bom        UTF-8 / UTF-16LE / UTF-16BE BOM present         confidence 1.0, hadBom
 *   3. declared   WebVTT is UTF-8 by spec; container charset       confidence 1.0
 *   4. heuristic  statistical detection                            0–1
 *
 * Then, ALWAYS: attempt UTF-8 strict first. Valid multi-byte UTF-8 is not an accident — if
 * the bytes decode without error and contain a multi-byte sequence, it is UTF-8, whatever a
 * frequency detector says. Only a decode ERROR lets the heuristic result apply.
 *
 * NORMALISATION applied before text reaches a parser is EXACTLY TWO THINGS:
 *   • strip the BOM (it is EncodingInfo.hadBom, not content)
 *   • normalise \r\n and lone \r to \n (line endings are WriteOptions.eol)
 * Nothing else. No Unicode normalisation, no trimming, no smart-quote folding — every one of
 * those is a CLEANING OP the user opted into, and doing it silently at decode makes the
 * before/after diff lie.
 */

import type { EncodingInfo } from '../../../contracts/cue-model.ts';

/** Below this, the UI must render the override control OPENED, not collapsed. */
export const LOW_CONFIDENCE = 0.7;

export interface DecodeRequest {
  readonly bytes: Uint8Array;
  /** An explicit override from the picker. Wins over everything. */
  readonly userLabel?: string | null;
  /** A charset the container or the format specification declares (WebVTT ⇒ 'utf-8'). */
  readonly declaredLabel?: string | null;
  /** For the WebVTT-is-UTF-8-by-spec rule and for nothing else. */
  readonly filename?: string | null;
}

export interface DecodeResult {
  readonly text: string;
  readonly encoding: EncodingInfo;
  /** True when the detector's confidence is below the threshold the UI opens the control at. */
  readonly needsOverride: boolean;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  BOM
───────────────────────────────────────────────────────────────────────────────────────*/

interface Bom {
  readonly label: string;
  readonly length: number;
}

export function detectBom(bytes: Uint8Array): Bom | null {
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return { label: 'utf-8', length: 3 };
  }
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe)
    return { label: 'utf-16le', length: 2 };
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff)
    return { label: 'utf-16be', length: 2 };
  return null;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  UTF-8 strict — implemented by hand, not by catching a TextDecoder throw.

  Why by hand: it also has to answer "does this contain a MULTI-BYTE sequence?", which is
  what turns "decodes cleanly" into "is definitely UTF-8". Pure ASCII decodes cleanly as
  UTF-8 AND as every single-byte codepage, so it proves nothing and must fall through to the
  heuristic. One pass answers both questions.
───────────────────────────────────────────────────────────────────────────────────────*/

export interface Utf8Check {
  readonly valid: boolean;
  readonly hasMultiByte: boolean;
  /** Byte offset of the first invalid sequence, for a diagnostic. */
  readonly errorAt: number;
}

export function checkUtf8(bytes: Uint8Array): Utf8Check {
  let i = 0;
  let hasMultiByte = false;
  const n = bytes.length;
  while (i < n) {
    const b = bytes[i] as number;
    if (b < 0x80) {
      i++;
      continue;
    }
    hasMultiByte = true;
    let need: number;
    let min: number;
    let cp: number;
    if (b >= 0xc2 && b <= 0xdf) {
      need = 1;
      min = 0x80;
      cp = b & 0x1f;
    } else if (b >= 0xe0 && b <= 0xef) {
      need = 2;
      min = 0x800;
      cp = b & 0x0f;
    } else if (b >= 0xf0 && b <= 0xf4) {
      need = 3;
      min = 0x10000;
      cp = b & 0x07;
    } else {
      return { valid: false, hasMultiByte, errorAt: i };
    }
    if (i + need >= n) return { valid: false, hasMultiByte, errorAt: i }; // truncated sequence
    for (let k = 1; k <= need; k++) {
      const c = bytes[i + k] as number;
      if ((c & 0xc0) !== 0x80) return { valid: false, hasMultiByte, errorAt: i + k };
      cp = (cp << 6) | (c & 0x3f);
    }
    // Overlong encodings, surrogates and out-of-range code points are all invalid UTF-8.
    if (cp < min || cp > 0x10ffff || (cp >= 0xd800 && cp <= 0xdfff)) {
      return { valid: false, hasMultiByte, errorAt: i };
    }
    i += need + 1;
  }
  return { valid: true, hasMultiByte, errorAt: -1 };
}

/*───────────────────────────────────────────────────────────────────────────────────────
  The heuristic.

  Zero dependencies: every in-browser detector worth using is 20–150 KB and this is on the
  critical path of every tool page. What the heuristic has to be good at is narrow — a
  subtitle file, already known not to be valid UTF-8, over the handful of legacy codepages
  subtitles actually ship in. It scores each candidate on how plausible its decoded output
  is, and it returns a CALIBRATED-ENOUGH number: the margin between the best and second-best
  candidate, which is exactly what the 0.7 threshold should mean.
───────────────────────────────────────────────────────────────────────────────────────*/

/** Candidate codepages, in the order a tie is broken. Latin-1 is absent on purpose: the
 *  WHATWG Encoding Standard aliases `iso-8859-1` to `windows-1252`, verified empirically. */
export const CANDIDATE_LABELS: readonly string[] = [
  'windows-1252', // western european — the default for a mislabelled file
  'windows-1250', // central european
  'windows-1251', // cyrillic
  'windows-1253', // greek
  'windows-1254', // turkish
  'windows-1255', // hebrew
  'windows-1256', // arabic
  'iso-8859-2',
  'iso-8859-5',
  'iso-8859-7',
  'koi8-r',
  'shift_jis',
  'gbk',
  'big5',
  'euc-kr',
];

interface Score {
  readonly label: string;
  readonly score: number;
}

/**
 * Scores a decode by looking for MOJIBAKE SIGNATURES rather than for "does it look like
 * text". Every candidate produces letters; that is why a naive letters-are-good score picks
 * windows-1252 for everything. What separates a right answer from a wrong one is the shape:
 *
 *   • a replacement character or a C1 control — near-proof the codepage is wrong
 *   • a SYMBOL or odd punctuation INSIDE a word (`Za¿ó³æ`) — the classic Latin-1-over-Latin-2
 *     signature, and the single strongest signal available
 *   • CASE FLIPPING inside a word (`ãÑÍÈÇ`) — what Arabic or Cyrillic read as windows-1252
 *     looks like, and what nothing real looks like
 *   • letters from THREE OR MORE scripts in one file
 *
 * Only NON-ASCII characters are scored: the ASCII part is identical under every candidate and
 * including it would drown the signal in the timestamp lines.
 */
function scoreDecoded(text: string): number {
  const chars = [...text];
  let sample = 0;
  let penalty = 0;
  const scripts = new Set<string>();

  const isLetter = (ch: string): boolean => /\p{L}/u.test(ch);
  const isUpper = (ch: string): boolean => /\p{Lu}/u.test(ch);
  const isLower = (ch: string): boolean => /\p{Ll}/u.test(ch);

  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i] as string;
    const cp = ch.codePointAt(0) as number;
    if (cp < 0x80) continue; // identical under every candidate
    sample++;

    if (cp === 0xfffd) {
      penalty += 4;
      continue;
    }
    if (cp <= 0x9f) {
      penalty += 3; // a C1 control never appears in subtitle text
      continue;
    }

    const prev = chars[i - 1] ?? '';
    const next = chars[i + 1] ?? '';

    if (!isLetter(ch)) {
      // A symbol or stray punctuation WITH LETTERS ON BOTH SIDES is mojibake, not text.
      if (isLetter(prev) && isLetter(next)) penalty += 3;
      else if (/[\p{S}]/u.test(ch)) penalty += 1;
      continue;
    }

    // HALF-WIDTH KATAKANA is the Shift_JIS mojibake signature: bytes 0xA1–0xDF decode to it,
    // so ANY single-byte text read as Shift_JIS comes out as a wall of ｱｲｳ. Real Japanese
    // subtitles use full-width kana. Without this, Shift_JIS ties with the right answer on
    // every Arabic and Cyrillic file.
    if (cp >= 0xff61 && cp <= 0xff9f) {
      penalty += 2;
      continue;
    }

    scripts.add(scriptFamily(cp));
    // Case flipping inside a word: real words capitalise at most their first letter.
    if (isLetter(prev)) {
      if (isUpper(ch) && isLower(prev)) penalty += 2;
      else if (isLower(ch) && isUpper(prev) && isLetter(chars[i - 2] ?? '')) penalty += 1;
    }
  }

  if (sample === 0) return 0;
  if (scripts.size >= 3) penalty += sample * 0.5;
  return Math.max(0, 1 - penalty / (sample * 2));
}

/** Coarse script family, for the "letters from three or more scripts" penalty. */
function scriptFamily(cp: number): string {
  if (cp < 0x0370) return 'latin';
  if (cp < 0x0400) return 'greek';
  if (cp < 0x0590) return 'cyrillic';
  if (cp < 0x0600) return 'hebrew';
  if (cp < 0x0700) return 'arabic';
  if (cp < 0x0980) return 'devanagari';
  if (cp < 0x0e00) return 'indic';
  if (cp < 0x0e80) return 'thai';
  if (cp < 0x3000) return 'other';
  if (cp < 0xa000) return 'cjk';
  if (cp < 0xd800) return 'hangul';
  return 'other';
}

/*───────────────────────────────────────────────────────────────────────────────────────
  windows-1252, decoded from OUR table rather than the runtime's.

  MEASURED, and the reason this exists: Node 24 (ICU 77.1) decodes windows-1252 bytes
  0x80–0x9F as C1 CONTROLS, which is ISO-8859-1 behaviour, not the WHATWG Encoding Standard
  index a BROWSER uses (0x80 → €, 0x97 → —, 0x93 → “). The label aliasing is right in both —
  `TextDecoder('iso-8859-1').encoding === 'windows-1252'` everywhere — but the C1 range is not.

  That difference is exactly where the smart quotes and em dashes in real subtitle files live,
  so a detector that trusted the runtime would score the same file differently in a test and
  in a browser. 27 table entries removes the whole class of environment dependence.
───────────────────────────────────────────────────────────────────────────────────────*/

const CP1252_C1: readonly number[] = [
  0x20ac, 0x0081, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030, 0x0160, 0x2039,
  0x0152, 0x008d, 0x017d, 0x008f, 0x0090, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014,
  0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, 0x009d, 0x017e, 0x0178,
];

export function decodeWindows1252(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i] as number;
    out += String.fromCodePoint(b >= 0x80 && b <= 0x9f ? (CP1252_C1[b - 0x80] as number) : b);
  }
  return out;
}

/** The labels the WHATWG standard aliases onto windows-1252. */
const CP1252_ALIASES = new Set([
  'windows-1252',
  'cp1252',
  'iso-8859-1',
  'iso8859-1',
  'iso_8859-1',
  'latin1',
  'l1',
  'ansi_x3.4-1968',
  'ascii',
  'us-ascii',
]);

function tryDecode(bytes: Uint8Array, label: string): string | null {
  if (CP1252_ALIASES.has(label.toLowerCase())) return decodeWindows1252(bytes);
  try {
    return new TextDecoder(label, { fatal: false }).decode(bytes);
  } catch {
    return null; // the runtime does not know this label
  }
}

/**
 * Runs every candidate and returns them best-first. Exported so a test can assert the
 * ranking rather than only the winner.
 */
export function scoreCandidates(
  bytes: Uint8Array,
  labels: readonly string[] = CANDIDATE_LABELS,
): readonly Score[] {
  const out: Score[] = [];
  for (const label of labels) {
    const text = tryDecode(bytes, label);
    if (text === null) continue;
    out.push({ label, score: scoreDecoded(text) });
  }
  return out.sort((a, b) => b.score - a.score || labels.indexOf(a.label) - labels.indexOf(b.label));
}

/**
 * Confidence from the candidate ranking.
 *
 * A raw plausibility score is NOT a confidence: windows-1252 and windows-1250 both score
 * ~0.99 on the same Polish file, and picking one of them at "0.99 confidence" is the exact
 * failure the 0.7 threshold exists to catch. So confidence is `best × (1 − runnerUp/best)`
 * — high only when one candidate is BOTH plausible AND clearly ahead. That makes the
 * contract's 0.7 mean what the UI needs it to mean: below it, show the override OPENED.
 */
export function confidenceFrom(
  scores: readonly Score[],
  sampleSize = Number.POSITIVE_INFINITY,
): number {
  const best = scores[0];
  if (best === undefined) return 0;
  // A tiny sample cannot support a confident answer no matter how it scores: two accented
  // characters look the same in six codepages.
  const evidence = Math.min(1, sampleSize / 8);
  const runnerUp = scores[1];
  if (runnerUp === undefined) return best.score * evidence;
  const margin = best.score === 0 ? 0 : 1 - runnerUp.score / best.score;
  // `best` says "this is plausible", `margin` says "and nothing else is as plausible".
  // Both are needed: windows-1252 and iso-8859-2 both score ~1.0 on `é ó ú`, and picking
  // one of them at 0.99 is exactly what the 0.7 threshold exists to prevent.
  return Math.max(0, Math.min(1, best.score * (0.35 + 0.65 * margin) * evidence));
}

/** Count of non-ASCII characters — the evidence a heuristic actually has to work with. */
export function nonAsciiCount(bytes: Uint8Array): number {
  let n = 0;
  for (let i = 0; i < bytes.length; i++) if ((bytes[i] as number) >= 0x80) n++;
  return n;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Normalisation — EXACTLY two things
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * Strips a leading U+FEFF and normalises CRLF and lone CR to LF. NOTHING ELSE.
 *
 * Smart quotes, NBSP, full-width characters and decomposed accents all survive byte-identical
 * into the parser. Doing any of that here would make the before/after diff lie about what the
 * user's cleaning op actually changed.
 */
export function normaliseForParser(text: string): string {
  const withoutBom = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  return withoutBom.replace(/\r\n?/g, '\n');
}

/*───────────────────────────────────────────────────────────────────────────────────────
  The entry point
───────────────────────────────────────────────────────────────────────────────────────*/

/** WebVTT is UTF-8 BY SPECIFICATION. That is a `declared`, not a guess. */
export function declaredForFilename(filename: string | null | undefined): string | null {
  if (!filename) return null;
  return /\.vtt$/i.test(filename) ? 'utf-8' : null;
}

export function decodeBytes(req: DecodeRequest): DecodeResult {
  const { bytes } = req;

  // 1 — user override
  if (req.userLabel) {
    const bom = detectBom(bytes);
    const body =
      bom !== null && sameLabel(bom.label, req.userLabel) ? bytes.subarray(bom.length) : bytes;
    const text = tryDecode(body, req.userLabel) ?? tryDecode(body, 'utf-8') ?? '';
    return result(text, {
      label: req.userLabel,
      confidence: 1,
      method: 'user',
      hadBom: bom !== null,
    });
  }

  // 2 — BOM
  const bom = detectBom(bytes);
  if (bom !== null) {
    const text = tryDecode(bytes.subarray(bom.length), bom.label) ?? '';
    return result(text, { label: bom.label, confidence: 1, method: 'bom', hadBom: true });
  }

  // 3 — declared (WebVTT by spec, container charset, HTTP charset)
  const declared = req.declaredLabel ?? declaredForFilename(req.filename);
  if (declared) {
    const text = tryDecode(bytes, declared);
    if (text !== null) {
      return result(text, { label: declared, confidence: 1, method: 'declared', hadBom: false });
    }
  }

  // ALWAYS: UTF-8 strict first. Valid multi-byte UTF-8 is not an accident.
  const utf8 = checkUtf8(bytes);
  if (utf8.valid && utf8.hasMultiByte) {
    const text = tryDecode(bytes, 'utf-8') ?? '';
    return result(text, { label: 'utf-8', confidence: 1, method: 'declared', hadBom: false });
  }
  if (utf8.valid && !utf8.hasMultiByte) {
    // Pure ASCII. Every candidate codepage decodes it identically, so there is nothing to
    // detect and nothing to be wrong about: utf-8 is the honest label at full confidence.
    const text = tryDecode(bytes, 'utf-8') ?? '';
    return result(text, { label: 'utf-8', confidence: 1, method: 'declared', hadBom: false });
  }

  // 4 — heuristic. Only a decode ERROR gets us here.
  const scores = scoreCandidates(bytes);
  const best = scores[0];
  const label = best?.label ?? 'windows-1252';
  const confidence = confidenceFrom(scores, nonAsciiCount(bytes));
  const text = tryDecode(bytes, label) ?? '';
  return result(text, { label, confidence, method: 'heuristic', hadBom: false });
}

function sameLabel(a: string, b: string): boolean {
  return a.toLowerCase().replace(/[-_]/g, '') === b.toLowerCase().replace(/[-_]/g, '');
}

function result(raw: string, encoding: EncodingInfo): DecodeResult {
  return {
    text: normaliseForParser(raw),
    encoding,
    needsOverride: encoding.method === 'heuristic' && encoding.confidence < LOW_CONFIDENCE,
  };
}
